import React, { useEffect, useState } from 'react';
import './SignIn.style.scss';
import logo from '../../assets/Logo/FBX_2024_alone.svg';
import spinner from '../../assets/Logo/FBX-Gif.gif';
import eyeIcon from '../../assets/icons/eye.svg';
import { version } from '../../Contexts/variables.jsx';

const AUTH_ENDPOINT = 'https://www.fbxchange.com/ReactPHP/reactSearchNew.php';
const LOGIN_VERSION_STORAGE_KEY = 'FBX_LOGIN_APP_VERSION';

const getResetTokenFromUrl = () => {
  const searchToken = new URLSearchParams(window.location.search).get('token');
  if (searchToken) return searchToken;

  const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
  return new URLSearchParams(hashQuery).get('token') || '';
};

const PasswordInput = ({ value, onChange, placeholder, disabled, autoComplete }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="password-input-wrap">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        disabled={disabled}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="password-eye-button"
        onClick={() => setVisible((current) => !current)}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
      >
        <img src={eyeIcon} alt="" aria-hidden="true" />
      </button>
    </div>
  );
};

const SignIn = ({ onSignIn }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [mode, setMode] = useState('signin');
  const [message, setMessage] = useState('');
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const refreshLoginCacheForNewVersion = async () => {
      try {
        const savedVersion = localStorage.getItem(LOGIN_VERSION_STORAGE_KEY);
        if (savedVersion === version) return;

        localStorage.setItem(LOGIN_VERSION_STORAGE_KEY, version);

        if ('caches' in window) {
          const cacheNames = await window.caches.keys();
          await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
        }

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('fbxv', version.replace(/[^0-9.]/g, ''));
        window.location.replace(nextUrl.toString());
      } catch {
        try {
          localStorage.setItem(LOGIN_VERSION_STORAGE_KEY, version);
        } catch {}
      }
    };

    refreshLoginCacheForNewVersion();
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setMessage('');
    setWorking(true);

    try {
      const result = onSignIn?.({ username, password });
      if (result && typeof result.then === 'function') {
        const signedIn = await result;
        if (signedIn === false) {
          setWorking(false);
        }
      }
    } catch {
      setWorking(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setWorking(true);
    setMessage('');

    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          method: 'requestPasswordReset',
          email: resetEmail,
        }).toString(),
      });

      const data = await response.json().catch(() => ({}));
      setMessage(data?.message || 'If that email is on an active FBXchange account, a reset link has been sent. If you do not receive the reset link, please contact Dan.');
    } catch {
      setMessage('Could not send the reset email. Please try again. If you do not receive the reset link, please contact Dan.');
    } finally {
      setWorking(false);
    }
  };

  const showForgot = mode === 'forgot';

  return (
    <div className="sign-in-container">
      <form className="sign-in-form" onSubmit={showForgot ? handleForgotSubmit : handleSignIn}>
        {showForgot && <h2>Reset Password</h2>}
        <div className="signin-logo-wrap mb-1">
          <img
            className="signin-logo-img"
            src={working ? spinner : logo}
            alt={working ? 'Working...' : 'FBX Logo'}
          />
        </div>

        <div className="signin-cache-note">
          FBX does not store cache, but if you experience problems with our site, other sites might have overloaded your cache causing FBX problems. Clearing your cache can solve those problems.
        </div>

        {showForgot ? (
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="Enter account email"
              required
              disabled={working}
            />
          </div>
        ) : (
          <>
            <div className="form-group">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                disabled={working}
              />
            </div>

            <div className="form-group">
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={working}
                autoComplete="current-password"
              />
            </div>
          </>
        )}

        {message && <div className="signin-message">{message}</div>}

        <button
          type="submit"
          className="sign-in-button"
          disabled={working}
          aria-busy={working}
        >
          {working ? 'Working...' : showForgot ? 'Send Reset Link' : 'Desktop Sign In'}
        </button>

        {!showForgot && (
          <a
            className="mobile-signin-button"
            href="https://www.fbxchange.com/FBXLite"
          >
            <span className="mobile-signin-icon" aria-hidden="true" />
            <span>Mobile Software</span>
          </a>
        )}

        <button
          type="button"
          className="signin-link-button"
          disabled={working}
          onClick={() => {
            setMode(showForgot ? 'signin' : 'forgot');
            setMessage('');
          }}
        >
          {showForgot ? 'Back to sign in' : 'Forgot password?'}
        </button>

        <div
          className="version-number text-center"
          style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#888' }}
        >
          {version}
        </div>
      </form>
    </div>
  );
};

export const PasswordReset = () => {
  const [token] = useState(getResetTokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [validating, setValidating] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [working, setWorking] = useState(false);

  React.useEffect(() => {
    let alive = true;

    const validateToken = async () => {
      if (!token) {
        setMessage('This reset link is invalid or has expired.');
        setValidating(false);
        return;
      }

      try {
        const response = await fetch(AUTH_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            method: 'validatePasswordResetToken',
            token,
          }).toString(),
        });
        const data = await response.json().catch(() => ({}));
        if (!alive) return;
        setValidToken(Boolean(data?.valid));
        setMessage(data?.message || '');
      } catch {
        if (alive) {
          setMessage('Could not validate this reset link.');
        }
      } finally {
        if (alive) {
          setValidating(false);
        }
      }
    };

    validateToken();

    return () => {
      alive = false;
    };
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    setMessage('');

    if (password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    setWorking(true);
    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          method: 'resetPassword',
          token,
          password,
        }).toString(),
      });
      const data = await response.json().catch(() => ({}));
      setMessage(data?.message || (data?.success ? 'Your password has been reset.' : 'Could not reset password.'));
      if (data?.success) {
        setValidToken(false);
        setPassword('');
        setConfirmPassword('');
      }
    } catch {
      setMessage('Could not reset password. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="sign-in-container">
      <form className="sign-in-form" onSubmit={handleReset}>
        <h2>Reset Password</h2>
        <div className="signin-logo-wrap mb-1">
          <img
            className="signin-logo-img"
            src={working || validating ? spinner : logo}
            alt={working || validating ? 'Working...' : 'FBX Logo'}
          />
        </div>

        {validToken && (
          <>
            <div className="form-group">
              <label>New Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={working}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={working}
                autoComplete="new-password"
              />
            </div>
          </>
        )}

        {message && <div className="signin-message">{message}</div>}

        {validToken && (
          <button type="submit" className="sign-in-button" disabled={working} aria-busy={working}>
            {working ? 'Working...' : 'Reset Password'}
          </button>
        )}

        <a className="signin-link-button signin-anchor" href="#/login">
          Back to sign in
        </a>
      </form>
    </div>
  );
};

export default SignIn;
